import React from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import DialogContent from '@material-ui/core/DialogContent';
import Dialog from '@material-ui/core/Dialog';
import { Box, IconButton, Typography } from '@material-ui/core';
import { X } from 'phosphor-react';
import moment from 'moment';
import { connect } from 'react-redux';

import { formatTime, showingTime } from '../../../lib/helpers';
import LogoLoader from '../LogoLoader';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    avatar: {
      backgroundColor: 'blue',
      color: 'blue',
    },
    paper: {
      borderRadius: 16,
      padding: 16,
      background: '#FFFFFF',
      width: 255,
    },
    content: {
      padding: 0,
      paddingTop: '0 !important',
    },
    title: {
      fontFamily: 'Sharp Grotesk Semibold !important',
      fontSize: '14px',
      color: '#000000',
    },
    closeButton: {
      position: 'absolute',
      top: 5,
      right: 10,
      padding: 10,
    },
    labelText: {
      fontSize: 10,
      fontWeight: 'bold',
      marginTop: 15,
    },
    dateText: {
      fontSize: 12,
      color: '#000000',
    },
    toggleButton: {
      height: 34,
      width: 40,
      padding: 0,
      textAlign: 'center',
      backgroundColor: '#E2E2E2',
      '&:hover': {
        background: '#cccccc',
      },
      color: '#ABABAB',
      fontWeight: 'bold',
      textTransform: 'none',
    },
    toggleLeftButton: {
      borderRadius: '8px 0px 0px 8px',
    },
    toggleRightButton: {
      borderLeft: '0.5px solid #cccccc',
      borderRadius: '0px 8px 8px 0px',
    },
    selected: {
      color: '#ffffff',
      backgroundColor: '#463264',
      '&:hover': {
        background: '#62478a',
      },
    },
    scheduleButton: {
      background: '#195A50',
      borderRadius: 24,
      height: 44,
      width: 100,
      '&:hover': {
        background: '#2e9283',
      },
      '&:disabled': {
        background: 'grey',
      },
    },
    buttonText: {
      color: '#ffffff',
      fontSize: 12,
      fontWeight: 'bold',
      margin: '0 10px',
      textTransform: 'none',
    },
    buttonContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginTop: 25,
    },
  }),
);

interface Props {
  deleteLoading: boolean;
  deleteShowing: (isOpenHouse?: boolean) => void;
  isEditing: boolean;
  onClose: Function;
  open: boolean;
  scheduleShowing: () => void;
  selectedDate: Date | undefined;
  selectedOpenHouse?: any;
  submitLoading: boolean;
}

export function OpenHouseDialog({
  deleteLoading,
  deleteShowing,
  isEditing,
  onClose,
  open,
  scheduleShowing,
  selectedDate,
  selectedOpenHouse,
  submitLoading,
}: Props) {
  const classes = useStyles();
  const [attending, setAttending] = React.useState<string | undefined>(
    undefined,
  );

  React.useEffect(() => {
    if (isEditing) {
      setAttending('yes');
    } else {
      setAttending('no');
    }
  }, [open, isEditing]);

  const handleClose = React.useCallback(() => {
    onClose();
    setAttending(undefined);
  }, []);

  const handleSubmit = () => {
    if (isEditing && attending === 'yes') {
      handleClose();
    } else if (isEditing && attending === 'no') {
      deleteShowing(true);
    } else if (!isEditing && attending === 'no') {
      handleClose();
    } else if (!isEditing && attending === 'yes') {
      scheduleShowing();
    }
  };

  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby="tour-dialog"
      classes={{ paper: classes.paper }}
      open={open}
    >
      <DialogContent classes={{ root: classes.content }}>
        <Typography className={classes.title}>Open House</Typography>
        <IconButton
          className={classes.closeButton}
          disabled={submitLoading || deleteLoading}
          onClick={handleClose}
        >
          <X size={16} color="#2D2332" weight="regular" />
        </IconButton>
        <Box display="flex" justifyContent="space-between" paddingRight="15px">
          <div>
            <p className={classes.labelText}>Date</p>
            <p className={classes.dateText}>
              {moment(selectedDate).format('ll')}
            </p>
          </div>
          <div>
            <p className={classes.labelText}>Time</p>
            {!isEditing && selectedOpenHouse ? (
              <p className={classes.dateText}>
                {formatTime(moment(selectedOpenHouse.start))} -{' '}
                {formatTime(moment(selectedOpenHouse.end), true)}
              </p>
            ) : (
              <p className={classes.dateText}>{showingTime(selectedDate)}</p>
            )}
          </div>
        </Box>
        <p className={classes.labelText} style={{ marginTop: 16 }}>
          Are you attending?
        </p>
        <Box display="flex" marginTop="15px">
          <Button
            className={`${classes.toggleButton} ${classes.toggleLeftButton}
            ${attending === 'yes' ? classes.selected : ''}`}
            onClick={() => setAttending('yes')}
          >
            Yes
          </Button>
          <Button
            className={`${classes.toggleButton} ${classes.toggleRightButton}
            ${attending === 'no' ? classes.selected : ''}`}
            onClick={() => setAttending('no')}
          >
            No
          </Button>
        </Box>
        <div className={classes.buttonContainer}>
          <Button
            className={classes.scheduleButton}
            classes={{ label: classes.buttonText }}
            onClick={handleSubmit}
            disabled={submitLoading || deleteLoading || !attending}
          >
            {submitLoading || deleteLoading ? (
              <LogoLoader imageProps={{ height: 40, width: 40 }} />
            ) : (
              'Confirm'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const mapStateToProps = (state: any) => ({
  submitLoading: state.showings.showingsSubmitLoading,
  deleteLoading: state.showings.showingDeleteLoading,
});

export default connect(mapStateToProps)(OpenHouseDialog);
